import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  AIProviderClientEnum,
  GlobalAIProviderSetting,
  SetGlobalAIProviderSettingParams,
  SetGlobalAIProviderSettingResponse
} from '@/types/aiProviderTypes';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Save, Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ProviderSettingsFormProps {
  provider: AIProviderClientEnum;
  currentSetting: GlobalAIProviderSetting | undefined;
}

const AI_PROVIDER_MODELS: Record<AIProviderClientEnum, string[]> = {
  [AIProviderClientEnum.OPENAI]: [
    'gpt-4o',
    'gpt-4-turbo',
    'gpt-4-turbo-preview',
    'gpt-4',
    'gpt-4-0125-preview',
    'gpt-4-1106-preview',
    'gpt-3.5-turbo',
    'gpt-3.5-turbo-0125',
    'gpt-3.5-turbo-1106',
  ],
  [AIProviderClientEnum.ANTHROPIC]: [
    'claude-3-opus-20240229',
    'claude-3-sonnet-20240229',
    'claude-3-haiku-20240307',
    'claude-2.1',
    'claude-2.0',
    'claude-instant-1.2',
  ],
  [AIProviderClientEnum.GOOGLE]: [
    'gemini-1.5-pro-latest',
    'gemini-1.5-flash-latest',
    'gemini-1.0-pro',
    'gemini-pro',
  ],
  [AIProviderClientEnum.AZURE]: [
    // Azure often uses deployment names. User will likely use 'Other...'
    // Or provide guidance on how to format them.
  ],
  [AIProviderClientEnum.MISTRAL]: [
    'mistral-large-latest',
    'mistral-medium-latest',
    'mistral-small-latest',
    'open-mistral-7b',
    'open-mixtral-8x7b',
  ],
  [AIProviderClientEnum.DEEPSEEK]: [
    'deepseek-chat',
    'deepseek-coder',
  ],
  [AIProviderClientEnum.CUSTOM]: [],
};

const OTHER_MODEL_VALUE = 'other_model';

const setGlobalAIProviderSetting = async (params: SetGlobalAIProviderSettingParams): Promise<SetGlobalAIProviderSettingResponse> => {
  const { data, error } = await supabase.rpc('set_global_ai_provider_setting', params);
  if (error) {
    console.error(`Error setting global AI setting for ${params.p_provider_name}:`, error);
    // If the function doesn't exist, provide a helpful error message
    if (error.message?.includes('function') && error.message?.includes('does not exist')) {
      throw new Error('Global AI provider settings are not available. Please apply the latest database migrations.');
    }
    throw new Error(error.message || 'Failed to save setting.');
  }
  const response = data as SetGlobalAIProviderSettingResponse;
  if (response.status !== 200) {
    throw new Error(response.message || `Failed to save setting (status ${response.status}). Details: ${response.details}`);
  }
  return response;
};

const ProviderSettingsForm: React.FC<ProviderSettingsFormProps> = ({ provider, currentSetting }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [apiKey, setApiKey] = useState<string>('');
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [selectedDropdownModel, setSelectedDropdownModel] = useState<string>('');
  const [customModelInput, setCustomModelInput] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  useEffect(() => {
    setBaseUrl(currentSetting?.base_url || '');
    setIsActive(currentSetting?.is_active || false);
    setNotes(currentSetting?.notes || '');
    setApiKey('');
    setShowApiKey(false);
    setHasChanged(false);

    const currentProviderModels = AI_PROVIDER_MODELS[provider] || [];
    const initialModel = currentSetting?.default_model || '';
    setDefaultModel(initialModel);

    if (initialModel && currentProviderModels.includes(initialModel)) {
      setSelectedDropdownModel(initialModel);
      setCustomModelInput('');
    } else if (initialModel) {
      setSelectedDropdownModel(OTHER_MODEL_VALUE);
      setCustomModelInput(initialModel);
    } else {
      setSelectedDropdownModel(currentProviderModels.length > 0 ? currentProviderModels[0] : OTHER_MODEL_VALUE);
      setCustomModelInput('');
      if (currentProviderModels.length > 0 && !initialModel) {
        setDefaultModel(currentProviderModels[0]);
      }
    }

  }, [currentSetting, provider]);

  const handleModelDropdownChange = (value: string) => {
    trackChange(setSelectedDropdownModel, value);
    if (value === OTHER_MODEL_VALUE) {
      trackChange(setDefaultModel, customModelInput);
    } else {
      trackChange(setDefaultModel, value);
      trackChange(setCustomModelInput, '');
    }
  };

  const handleCustomModelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const customValue = e.target.value;
    trackChange(setCustomModelInput, customValue);
    if (selectedDropdownModel === OTHER_MODEL_VALUE) {
      trackChange(setDefaultModel, customValue);
    }
  };

  const mutation = useMutation<SetGlobalAIProviderSettingResponse, Error, SetGlobalAIProviderSettingParams>({
    mutationFn: setGlobalAIProviderSetting,
    onSuccess: (data) => {
      toast({
        title: "Settings Saved",
        description: `Successfully updated settings for ${data.provider_name}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['globalAISettings'] });
      setHasChanged(false);
      setApiKey('');
      setShowApiKey(false);
    },
    onError: (error) => {
      toast({
        title: "Error Saving Settings",
        description: error.message || "An unexpected error occurred.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params: SetGlobalAIProviderSettingParams = { p_provider_name: provider };
    let effectiveChanges = false;

    if (apiKey !== '' || (apiKey === '' && currentSetting?.has_api_key)) {
        params.p_api_key = apiKey;
        effectiveChanges = true;
    }

    if (baseUrl !== (currentSetting?.base_url || '')) {
      params.p_base_url = baseUrl;
      effectiveChanges = true;
    }
    if (defaultModel !== (currentSetting?.default_model || '')) {
      params.p_default_model = defaultModel;
      effectiveChanges = true;
    }
    if (isActive !== (currentSetting?.is_active || false)) {
      params.p_is_active = isActive;
      effectiveChanges = true;
    }
    if (notes !== (currentSetting?.notes || '')) {
      params.p_notes = notes;
      effectiveChanges = true;
    }

    if (!effectiveChanges) {
        toast({ title: "No Effective Changes", description: "No settings were modified that would result in an update.", variant: "default" });
        setHasChanged(false);
        return;
    }
    mutation.mutate(params);
  };

  const trackChange = <T extends unknown>(setter: React.Dispatch<React.SetStateAction<T>>, value: T) => {
    setter(value);
    if (!hasChanged) setHasChanged(true);
  }

  const availableModels = AI_PROVIDER_MODELS[provider] || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor={`${provider}-apiKey`}>API Key {currentSetting?.has_api_key ? "(Set - Enter new to update or empty to clear)" : "(Not Set)"}</Label>
        <div className="relative flex items-center">
            <Input
                id={`${provider}-apiKey`}
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => trackChange(setApiKey, e.target.value)}
                placeholder={currentSetting?.has_api_key ? "Enter new key to update, or empty to clear" : "Enter API Key"}
                className="pr-10"
            />
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowApiKey(!showApiKey)}
                tabIndex={-1}
            >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
        </div>
        <p className='text-xs text-muted-foreground mt-1'>To update a key, enter the new value. To clear a set key, submit an empty field. If no key is set, leave blank to keep it unset.</p>
      </div>

      <div>
        <Label htmlFor={`${provider}-baseUrl`}>Base URL (Optional)</Label>
        <Input
          id={`${provider}-baseUrl`}
          value={baseUrl}
          onChange={(e) => trackChange(setBaseUrl, e.target.value)}
          placeholder="e.g., https://api.example.com/v1 (for Azure, custom models)"
        />
      </div>

      <div>
        <Label htmlFor={`${provider}-defaultModel`}>Default Model (Optional)</Label>
        <Select onValueChange={handleModelDropdownChange} value={selectedDropdownModel}>
          <SelectTrigger id={`${provider}-defaultModel-select`}>
            <SelectValue placeholder="Select a model or choose 'Other'" />
          </SelectTrigger>
          <SelectContent>
            {availableModels.map((model) => (
              <SelectItem key={model} value={model}>
                {model}
              </SelectItem>
            ))}
            <SelectItem value={OTHER_MODEL_VALUE}>
              Other...
            </SelectItem>
          </SelectContent>
        </Select>
        {selectedDropdownModel === OTHER_MODEL_VALUE && (
          <Input
            id={`${provider}-customDefaultModel`}
            value={customModelInput}
            onChange={handleCustomModelInputChange}
            placeholder="Enter custom model name (e.g., gpt-3.5-turbo-instruct)"
            className="mt-2"
          />
        )}
      </div>

      <div className="flex items-center space-x-2 pt-2">
        <Switch
          id={`${provider}-isActive`}
          checked={isActive}
          onCheckedChange={(checked) => trackChange(setIsActive, checked)}
        />
        <Label htmlFor={`${provider}-isActive`} className="cursor-pointer">
          Activate this global provider configuration
        </Label>
      </div>

      <div>
        <Label htmlFor={`${provider}-notes`}>Notes (Optional)</Label>
        <Textarea
            id={`${provider}-notes`}
            value={notes}
            onChange={(e) => trackChange(setNotes, e.target.value)}
            placeholder="Administrative notes, e.g., key rotation date, account info (not for sensitive data)"
            rows={3}
        />
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={mutation.isPending || !hasChanged} className='w-full sm:w-auto min-w-[120px]'>
          {mutation.isPending ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
          ) : (
            <><Save className="mr-2 h-4 w-4" /> Save</>
          )}
        </Button>
      </div>
      {currentSetting && !currentSetting.is_active && (
          <Alert variant="default" className='mt-4 bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-400'>
            <AlertTriangle className="h-4 w-4 !text-amber-600 dark:!text-amber-400" />
            <AlertTitle className="font-semibold">Provider Inactive</AlertTitle>
            <AlertDescription>
                This global provider configuration is currently inactive and will not be used as a fallback by chatbots.
            </AlertDescription>
        </Alert>
      )}
    </form>
  );
};

export default ProviderSettingsForm;
